import type { AcledEvent, AcledEventType, ThreatLevel } from '@/types';

// ---------------------------------------------------------------------------
// ACLED — Armed Conflict Location & Event Data
// API docs: https://acleddata.com/resources/general-guides/#1603120850965-5a7e40ba-37fd
//
// In dev: calls /api/acled (Vite middleware, 30-min cache)
// Requires env vars: VITE_ACLED_KEY + VITE_ACLED_EMAIL (free at acleddata.com)
// Without API key: returns curated static fallback for key conflict zones
// ---------------------------------------------------------------------------

interface RawAcledEvent {
  event_id_cnty: string;
  event_date: string;
  event_type: string;
  sub_event_type: string;
  actor1: string;
  actor2: string;
  country: string;
  location: string;
  latitude: string;
  longitude: string;
  fatalities: string;
  notes: string;
  source: string;
}

interface AcledApiResponse {
  data: RawAcledEvent[];
  fallback?: boolean;
  error?: string;
}

function deriveServerity(eventType: string, fatalities: number): ThreatLevel {
  if (eventType === 'Battles' && fatalities >= 10) return 'critical';
  if (eventType === 'Battles') return 'high';
  if (eventType === 'Explosions/Remote violence' && fatalities >= 5) return 'critical';
  if (eventType === 'Explosions/Remote violence') return 'high';
  if (eventType === 'Violence against civilians') return 'high';
  if (eventType === 'Riots') return 'medium';
  if (eventType === 'Protests') return 'low';
  return 'info';
}

function parseRaw(raw: RawAcledEvent): AcledEvent | null {
  const lat = parseFloat(raw.latitude);
  const lon = parseFloat(raw.longitude);
  if (isNaN(lat) || isNaN(lon)) return null;
  const fatalities = parseInt(raw.fatalities, 10) || 0;
  return {
    id: raw.event_id_cnty,
    event_date: raw.event_date,
    event_type: raw.event_type as AcledEventType,
    sub_event_type: raw.sub_event_type,
    actor1: raw.actor1,
    actor2: raw.actor2,
    country: raw.country,
    location: raw.location,
    lat,
    lon,
    fatalities,
    notes: raw.notes?.slice(0, 400) ?? '',
    source: raw.source,
    severity: deriveServerity(raw.event_type, fatalities),
  };
}

// ---------------------------------------------------------------------------
// Static fallback dataset — key ongoing conflicts (curated OSINT)
// Used when ACLED API key not configured
// ---------------------------------------------------------------------------

const STATIC_FALLBACK: AcledEvent[] = [
  // Ukraine — front line
  { id: 'static-ua-1', event_date: new Date().toISOString(), event_type: 'Battles', sub_event_type: 'Armed clash', actor1: 'Russian Armed Forces', actor2: 'Ukrainian Armed Forces', country: 'Ukraine', location: 'Bakhmut region', lat: 48.60, lon: 37.97, fatalities: 0, notes: 'Ongoing frontline combat operations', source: 'OSINT', severity: 'critical' },
  { id: 'static-ua-2', event_date: new Date().toISOString(), event_type: 'Explosions/Remote violence', sub_event_type: 'Air/drone strike', actor1: 'Russian Armed Forces', actor2: 'Civilians', country: 'Ukraine', location: 'Kharkiv', lat: 49.99, lon: 36.23, fatalities: 0, notes: 'Artillery and drone strikes on infrastructure', source: 'OSINT', severity: 'high' },
  { id: 'static-ua-3', event_date: new Date().toISOString(), event_type: 'Explosions/Remote violence', sub_event_type: 'Shelling/artillery/missile attack', actor1: 'Russian Armed Forces', actor2: 'Ukrainian Armed Forces', country: 'Ukraine', location: 'Zaporizhzhia', lat: 47.84, lon: 35.14, fatalities: 0, notes: 'Artillery exchanges across contact line', source: 'OSINT', severity: 'high' },
  // Gaza / Israel
  { id: 'static-gz-1', event_date: new Date().toISOString(), event_type: 'Battles', sub_event_type: 'Armed clash', actor1: 'Israeli Forces', actor2: 'Hamas', country: 'Palestine', location: 'Gaza City', lat: 31.50, lon: 34.47, fatalities: 0, notes: 'Ongoing IDF ground operations', source: 'OSINT', severity: 'critical' },
  { id: 'static-gz-2', event_date: new Date().toISOString(), event_type: 'Explosions/Remote violence', sub_event_type: 'Air/drone strike', actor1: 'Israeli Forces', actor2: 'Hamas', country: 'Palestine', location: 'Rafah', lat: 31.28, lon: 34.25, fatalities: 0, notes: 'Air strikes on Rafah', source: 'OSINT', severity: 'critical' },
  { id: 'static-gz-3', event_date: new Date().toISOString(), event_type: 'Explosions/Remote violence', sub_event_type: 'Air/drone strike', actor1: 'Israeli Forces', actor2: 'Hezbollah', country: 'Lebanon', location: 'South Lebanon', lat: 33.27, lon: 35.55, fatalities: 0, notes: 'IDF strikes on Hezbollah positions', source: 'OSINT', severity: 'high' },
  // Sudan
  { id: 'static-sd-1', event_date: new Date().toISOString(), event_type: 'Battles', sub_event_type: 'Armed clash', actor1: 'Sudanese Armed Forces', actor2: 'Rapid Support Forces', country: 'Sudan', location: 'Khartoum', lat: 15.55, lon: 32.53, fatalities: 0, notes: 'SAF vs RSF urban fighting', source: 'OSINT', severity: 'critical' },
  { id: 'static-sd-2', event_date: new Date().toISOString(), event_type: 'Battles', sub_event_type: 'Armed clash', actor1: 'Rapid Support Forces', actor2: 'Civilians', country: 'Sudan', location: 'El Fasher', lat: 13.63, lon: 25.35, fatalities: 0, notes: 'RSF siege of El Fasher continues', source: 'OSINT', severity: 'critical' },
  // Yemen
  { id: 'static-ye-1', event_date: new Date().toISOString(), event_type: 'Explosions/Remote violence', sub_event_type: 'Air/drone strike', actor1: 'Houthi Forces', actor2: 'Saudi-led Coalition', country: 'Yemen', location: 'Hodeidah', lat: 14.80, lon: 42.95, fatalities: 0, notes: 'Houthi drone/missile activity in Red Sea corridor', source: 'OSINT', severity: 'high' },
  { id: 'static-ye-2', event_date: new Date().toISOString(), event_type: 'Explosions/Remote violence', sub_event_type: 'Remote explosive/landmine/IED', actor1: 'Houthi Forces', actor2: 'Red Sea shipping', country: 'Yemen', location: 'Red Sea', lat: 15.00, lon: 43.00, fatalities: 0, notes: 'Houthi attacks on commercial shipping', source: 'OSINT', severity: 'high' },
  // Myanmar
  { id: 'static-mm-1', event_date: new Date().toISOString(), event_type: 'Battles', sub_event_type: 'Armed clash', actor1: 'Tatmadaw', actor2: 'People\'s Defence Force', country: 'Myanmar', location: 'Rakhine State', lat: 20.14, lon: 92.89, fatalities: 0, notes: 'Arakan Army advances in Rakhine', source: 'OSINT', severity: 'high' },
  // Iran — potential flashpoint
  { id: 'static-ir-1', event_date: new Date().toISOString(), event_type: 'Strategic developments', sub_event_type: 'Looting/property destruction', actor1: 'IRGC', actor2: 'Unknown', country: 'Iran', location: 'Tehran', lat: 35.69, lon: 51.39, fatalities: 0, notes: 'Elevated military activity — nuclear negotiations ongoing', source: 'OSINT', severity: 'medium' },
  // Korean Peninsula
  { id: 'static-kp-1', event_date: new Date().toISOString(), event_type: 'Strategic developments', sub_event_type: 'Disruption of activities (government forces)', actor1: 'Korean People\'s Army', actor2: 'Republic of Korea Armed Forces', country: 'North Korea', location: 'DMZ', lat: 38.31, lon: 127.14, fatalities: 0, notes: 'DPRK military exercises near DMZ', source: 'OSINT', severity: 'medium' },
  // Somalia / Sahel
  { id: 'static-so-1', event_date: new Date().toISOString(), event_type: 'Explosions/Remote violence', sub_event_type: 'Remote explosive/landmine/IED', actor1: 'Al-Shabaab', actor2: 'ATMIS', country: 'Somalia', location: 'Mogadishu', lat: 2.04, lon: 45.34, fatalities: 0, notes: 'Al-Shabaab IED attacks in southern Somalia', source: 'OSINT', severity: 'high' },
  { id: 'static-ml-1', event_date: new Date().toISOString(), event_type: 'Battles', sub_event_type: 'Armed clash', actor1: 'JNIM', actor2: 'Mali Armed Forces', country: 'Mali', location: 'Mopti', lat: 14.49, lon: -4.20, fatalities: 0, notes: 'Jihadist activity in central Mali', source: 'OSINT', severity: 'high' },
];

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

let _cache: AcledEvent[] | null = null;
let _cacheTs = 0;
const CLIENT_CACHE_TTL = 15 * 60_000; // 15 min client-side cache

/**
 * Fetch ACLED conflict events.
 * In dev: calls /api/acled (Vite middleware). Falls back to static dataset.
 * @param days  — How many days back to query (default 30)
 */
export async function fetchAcledEvents(days = 30): Promise<AcledEvent[]> {
  const now = Date.now();
  if (_cache && now - _cacheTs < CLIENT_CACHE_TTL) return _cache;

  try {
    const res = await fetch(`/api/acled?days=${days}`, {
      signal: AbortSignal.timeout(25_000),
    });
    if (res.ok) {
      const json: AcledApiResponse = await res.json();
      if (json.fallback || !json.data?.length) {
        // No API key configured — use static fallback
        _cache = STATIC_FALLBACK;
        _cacheTs = now;
        return _cache;
      }
      const events = (json.data ?? [])
        .map(parseRaw)
        .filter((e): e is AcledEvent => e !== null);
      _cache = events;
      _cacheTs = now;
      console.log(`[ACLED] ${events.length} events loaded`);
      return events;
    }
  } catch (err) {
    console.warn('[ACLED] API call failed, using static fallback:', err);
  }

  // Static fallback for prod or when API fails
  _cache = STATIC_FALLBACK;
  _cacheTs = now;
  return _cache;
}

/** Color by ACLED event type */
export const ACLED_COLORS: Record<string, [number, number, number, number]> = {
  'Battles':                    [220, 38,  38,  220], // red
  'Explosions/Remote violence': [249, 115, 22,  220], // orange
  'Violence against civilians': [236, 72,  153, 200], // pink
  'Protests':                   [250, 204, 21,  180], // yellow
  'Riots':                      [234, 179, 8,   180], // amber
  'Strategic developments':     [148, 163, 184, 160], // slate
};
